import { Box, styled as muiStyled, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import styled from "styled-components";

import { ScrollToTop } from "@/Components/v2/atoms/ScrollToTop";
import { Drawer } from "@/Components/v2/organism/Drawer";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { colors, GlobalStyle } from "@/Utils";
import {
  breakpoints,
  CONTAINER_WIDTH_PX,
  DRAWER_BREAKPOINT,
  DRAWER_COLLAPSED_WIDTH_PX,
  DRAWER_WIDTH_PX,
  TOP_NAV_LAYOUT_CONTAINER_WIDTH_PX,
  TOP_NAV_CONTENT_MIN_WIDTH_PX,
  TOP_NAV_CONTENT_MAX_WIDTH_PX,
} from "@/Utils/GlobalStyle";

import { ToastContainer } from "@/Components/v2/molecule/ToastContainer/ToastContainer";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import Navigation from "@/Components/v2/organism/Navigation/Navigation";
import { DrawerV2 } from "@/Components/v2/organism/Drawer/DrawerV2";
import { useFeatureGate } from "@statsig/react-bindings";

const FullWidthContentWrapper = styled.div`
  height: calc(100% - 60px);
  min-width: ${CONTAINER_WIDTH_PX}px;
  max-width: ${breakpoints.xl}px;
  padding-left: ${DRAWER_COLLAPSED_WIDTH_PX}px;
  margin: 0 auto;

  @media (min-width: ${DRAWER_BREAKPOINT}px) {
    padding-left: ${DRAWER_WIDTH_PX}px;
  }
`;

const FullWidthContentWrapperTopNav = styled.div`
  position: relative;
  padding-top: 60px;
  padding-bottom: 20px;
  @media (max-width: ${breakpoints.xl + DRAWER_WIDTH_PX}px) {
    padding-left: ${DRAWER_WIDTH_PX}px;
  }
  @media (max-width: ${DRAWER_BREAKPOINT - 1}px) {
    padding-left: 20px;
  }
`;

const FullContentWrapper = muiStyled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  minWidth: `${CONTAINER_WIDTH_PX}px`,
  [`@media (min-width: ${DRAWER_BREAKPOINT}px)`]: {
    paddingLeft: `${DRAWER_WIDTH_PX}px`,
  },
});

const FullWidthMainApp = styled.div`
  width: 100%;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => (props.theme.mode === "dark" ? colors.lightBlack : colors.white)};
  margin-top: 1rem;
`;

const FullContentWrapperTopNav = muiStyled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  width: "100%",
  margin: "0 auto",
  paddingTop: "62px",
});

const FullWidthMainAppTopNav = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  position: relative;
  padding-left: 0;
`;

const DrawerContainerTopNav = muiStyled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: `${DRAWER_WIDTH_PX}px`,
  position: "fixed",
  left: 0,
  top: "62px",
  height: "calc(100vh - 62px)",
  overflowY: "auto",
  overflowX: "visible",
  zIndex: 1000,
  borderRight: `1px solid ${theme.palette.navigation.border}`,
  [`@media (max-width: ${DRAWER_BREAKPOINT - 1}px)`]: {
    width: "120px",
    borderRight: "none",
    overflow: "visible",
  },
}));

const MainWrapperTopNav = styled.div`
  min-width: ${TOP_NAV_CONTENT_MIN_WIDTH_PX}px;
  max-width: ${TOP_NAV_CONTENT_MAX_WIDTH_PX}px;
  width: 100%;
  margin-right: auto;
  padding-left: calc((100vw - ${DRAWER_WIDTH_PX}px - ${TOP_NAV_CONTENT_MAX_WIDTH_PX}px) / 2);
  padding-top: 40px;
  @media (min-width: ${breakpoints.xl + DRAWER_WIDTH_PX}px) {
    margin-left: ${DRAWER_WIDTH_PX}px;
  }
  @media (max-width: ${DRAWER_BREAKPOINT - 1}px) {
    margin: 0 ${DRAWER_COLLAPSED_WIDTH_PX}px;
    padding-left: 0;
    width: auto;
    max-width: ${TOP_NAV_LAYOUT_CONTAINER_WIDTH_PX + DRAWER_COLLAPSED_WIDTH_PX}px;
  }
  & > .MuiBox-root:first-child > .MuiStack-root:first-child {
    padding-top: 0;
  }
`;

const ScenarioBuilderWrapper = muiStyled(FullContentWrapperTopNav)({
  minWidth: `${TOP_NAV_CONTENT_MIN_WIDTH_PX}px`,
  maxWidth: `${TOP_NAV_CONTENT_MAX_WIDTH_PX}px`,
  width: "100%",
  margin: "0 auto",
});

const TopNavLayout = ({ children, theme }) => {
  const location = useLocation();

  const isFullPageContent = useMemo(() => {
    return location.pathname.includes("scenarios-v3");
  }, [location]);

  const isCustomLeftNavPage = useMemo(() => {
    return location.pathname.includes("maite");
  }, [location]);

  return (
    <ThemeProvider theme={theme}>
      <ScrollToTop />
      <ToastContainer />
      <GlobalStyle />
      <Navigation />
      {isFullPageContent ? (
        <ScenarioBuilderWrapper>{children}</ScenarioBuilderWrapper>
      ) : isCustomLeftNavPage ? (
        <FullContentWrapperTopNav>{children}</FullContentWrapperTopNav>
      ) : (
        <FullWidthContentWrapperTopNav>
          <FullWidthMainAppTopNav id="appDiv">
            <DrawerContainerTopNav>
              <DrawerV2 />
            </DrawerContainerTopNav>
            <MainWrapperTopNav id="main-wrapper-top-nav">{children}</MainWrapperTopNav>
          </FullWidthMainAppTopNav>
        </FullWidthContentWrapperTopNav>
      )}
    </ThemeProvider>
  );
};

const LandingCard = (props) => {
  const { theme } = useInfo();
  const location = useLocation();
  const { value: isTopNavLayoutEnabled } = useFeatureGate(featureFlag.topNavLayout);

  const isFullPageContent = useMemo(() => {
    return location.pathname.includes("maite");
  }, [location]);

  if (isTopNavLayoutEnabled) {
    return <TopNavLayout theme={theme}>{props.children}</TopNavLayout>;
  }

  return (
    <ThemeProvider theme={theme}>
      <ScrollToTop />
      <ToastContainer />
      <GlobalStyle />
      <Drawer />
      {isFullPageContent ? (
        <FullContentWrapper>{props.children}</FullContentWrapper>
      ) : (
        <FullWidthContentWrapper>
          <FullWidthMainApp id="appDiv">
            <MainWrapper>{props.children}</MainWrapper>
          </FullWidthMainApp>
        </FullWidthContentWrapper>
      )}
    </ThemeProvider>
  );
};

export default LandingCard;
